import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";

const ProgramAccessContext = createContext(null);

/**
 * Whether the signed-in member has reached the "My Program" side of the journey.
 *
 * Shared through context because both the navigation label and the Program page
 * itself depend on the answer, and neither should pay for its own request.
 */
export const ProgramAccessProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setHasAccess(false);
      setRequest(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data } = await apiClient.get("/program-request");
      setHasAccess(Boolean(data.has_program_access));
      setRequest(data.request ?? null);
    } catch {
      // Treat an unreachable check as "no access": the request page is the
      // safe landing spot, and it surfaces its own error if it also fails.
      setHasAccess(false);
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  const value = useMemo(
    () => ({ hasAccess, request, loading: loading || authLoading, refresh: load }),
    [hasAccess, request, loading, authLoading, load],
  );

  return (
    <ProgramAccessContext.Provider value={value}>
      {children}
    </ProgramAccessContext.Provider>
  );
};

export const useProgramAccess = () => {
  const context = useContext(ProgramAccessContext);

  if (!context) {
    throw new Error("useProgramAccess must be used inside ProgramAccessProvider");
  }

  return context;
};
